import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchMyProfile, updateMyProfile } from '@/features/users/api'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'

/**
 * Self-service "My Profile" page, reachable by any authenticated role via
 * the header. Previously the only profile view was UserDetailPage's
 * (users.view-gated) self-edit branch and app/views/admin.py's
 * admin-only /admin/profile - neither reachable by a plain Staff/Client
 * user without users.view, so most users had no working profile page at
 * all. Backed by the new /portal/users/api/v2/me + /portal/users/update/me
 * endpoints, which are identity-gated (always current_user, never a
 * caller-supplied id) rather than permission-gated.
 */
export default function ProfilePage() {
  const queryClient = useQueryClient()

  const profileQuery = useQuery({
    queryKey: ['my-profile'],
    queryFn: fetchMyProfile,
  })

  const [firstname, setFirstname] = useState('')
  const [lastname, setLastname] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!profileQuery.data) return
    setFirstname(profileQuery.data.firstname ?? '')
    setLastname(profileQuery.data.lastname ?? '')
    setPhone(profileQuery.data.phone ?? '')
  }, [profileQuery.data])

  const saveMutation = useMutation({
    mutationFn: () => updateMyProfile({ firstname, lastname, phone, password: password || undefined }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['my-profile'], updated)
      setPassword('')
      setSaved(true)
    },
  })

  if (profileQuery.isLoading) return <LoadingState label="Loading profile…" />
  if (profileQuery.isError) {
    return (
      <ErrorState
        message={profileQuery.error instanceof Error ? profileQuery.error.message : 'Failed to load profile.'}
        onRetry={() => profileQuery.refetch()}
      />
    )
  }
  const user = profileQuery.data
  if (!user) return null

  const roleLabel = user.is_admin || user.is_super_user ? 'Administrator' : user.is_staff ? 'Staff' : 'Client'

  return (
    <div className="max-w-xl space-y-6">
      <h1 className="text-xl font-semibold text-text-primary">My Profile</h1>

      <section className="rounded border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-primary">Account</h2>
          <span className="rounded bg-bg-secondary px-2 py-0.5 text-xs text-text-secondary">{roleLabel}</span>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            setSaved(false)
            saveMutation.mutate()
          }}
          className="space-y-3"
        >
          <Field label="First name" value={firstname} onChange={setFirstname} />
          <Field label="Last name" value={lastname} onChange={setLastname} />
          <Field label="Email" value={user.email} onChange={() => {}} disabled />
          <Field label="Phone" value={phone} onChange={setPhone} />
          {user.company_name && <Field label="Company" value={user.company_name} onChange={() => {}} disabled />}
          <Field
            label="New password (leave blank to keep unchanged)"
            value={password}
            onChange={(v) => {
              setPassword(v)
              setSaved(false)
            }}
            type="password"
          />

          {saveMutation.isError && (
            <p className="text-xs text-error">
              {saveMutation.error instanceof Error ? saveMutation.error.message : 'Failed to save profile.'}
            </p>
          )}
          {saved && !saveMutation.isPending && <p className="text-xs text-brand">Profile saved.</p>}

          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save Profile'}
          </button>
        </form>
      </section>
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  disabled,
  type = 'text',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  type?: string
}) {
  return (
    <div>
      <label className="mb-1 block text-xs text-text-secondary">{label}</label>
      <input
        type={type}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary disabled:opacity-60"
      />
    </div>
  )
}
